// Admin, in two tabs. The daily: site numbers, analytics, new players by source, daily reminders, uploaded
// photos, and the feedback inbox with each mode's rating tally. Slow Burn: that mode's runs and how they went. Only members whose email is
// in the server's ADMINS list get here (Me.admin); everyone else sees the not-found page.
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { RATING_MODES, type AdminAvatars, type AdminOverview, type FeedbackItem, type FeedbackPage, type FeedbackStatus, type RatingMode, type ReminderDay, type ReminderOutcome, type SourceRow } from '@wbwl/contract'
import { Analytics } from '../components/analytics-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { SlowBurnStats } from '../components/slow-burn-admin.tsx'
import { SlowBurnMark } from '../components/slow-burn-ui.tsx'
import { Avatar, ErrorNote, NotFound, useToast } from '../components/shell.tsx'
import { api } from '../lib/api.ts'
import { useAdminAvatars, useAdminFeedback, useAdminOverview, useAdminReminders, useAdminSources, useMe } from '../lib/data.ts'
import { fmt } from '../lib/game.ts'
import { getResource, invalidate, setResource } from '../lib/store.ts'

export default function AdminPage() {
  const me = useMe().data
  if (!me?.admin) return <NotFound />
  return <Admin />
}

function Admin() {
  const [params, setParams] = useSearchParams()
  const slowBurn = params.get('tab') === 'slow-burn'
  return (
    <div className="page admin">
      <div className="topbar">
        <Link className="ibtn" to="/you" aria-label="Back to settings"><Icon name="back" /></Link>
        <div className="title"><b>Admin</b><small>ADMINS ONLY</small></div>
      </div>
      <nav className="lb-modes admin-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={!slowBurn} className={slowBurn ? '' : 'on'} onClick={() => setParams({}, { replace: true })}>The daily</button>
        <button type="button" role="tab" aria-selected={slowBurn} className={slowBurn ? 'on' : ''} onClick={() => setParams({ tab: 'slow-burn' }, { replace: true })}><SlowBurnMark />Slow Burn</button>
      </nav>
      {slowBurn ? <SlowBurnStats /> : <Daily />}
    </div>
  )
}

/** The daily game and the site: everything the admin page had before Slow Burn. */
function Daily() {
  const overview = useAdminOverview()
  const [status, setStatus] = useState<FeedbackStatus>('open')
  const o = overview.data
  const n = (v: number | undefined) => (v === undefined ? '—' : fmt(v))
  return (
    <>
      {overview.error ? <ErrorNote error={overview.error} retry={() => overview.reload()} /> : (
        <div className="stat-grid admin-stats">
          <div className="stat"><b>{n(o?.today.players)}</b><span>finished<br />{o ? `No. ${o.today.edition} today` : 'today'}</span></div>
          <div className="stat"><b>{n(o?.members)}</b><span>members<br />have an account</span></div>
          <div className="stat">
            <b>{n(o?.guests.played)}</b><span>guests<br />played, no account</span>
            {o && <span className="more">+{fmt(o.guests.visited)} visited, never played</span>}
          </div>
          <div className="stat"><b>{n(o?.openFeedback)}</b><span>feedback<br />waiting</span></div>
        </div>
      )}
      <Analytics />
      <Sources />
      <Reminders />
      <Uploads />
      <section className="you-sec">
        <div className="sec-head"><h3>Feedback</h3></div>
        {o && <RatingTallies ratings={o.ratings} />}
        <div className="seg" role="tablist">
          <button type="button" role="tab" aria-selected={status === 'open'} className={status === 'open' ? 'on' : ''} onClick={() => setStatus('open')}>Open{o ? ` · ${fmt(o.openFeedback)}` : ''}</button>
          <button type="button" role="tab" aria-selected={status === 'done'} className={status === 'done' ? 'on' : ''} onClick={() => setStatus('done')}>Done</button>
        </div>
        <FeedbackList status={status} />
      </section>
    </>
  )
}

/** Photos players uploaded, newest first, any of which can be taken down. */
function Uploads() {
  const uploads = useAdminAvatars()
  const toast = useToast()
  async function remove(id: string, name: string) {
    if (!window.confirm(`Take down ${name}’s photo? They go back to their initials.`)) return
    try {
      await api.del(`/admin/avatars/${id}`)
      const now = getResource<AdminAvatars>('admin:avatars')
      if (now) setResource('admin:avatars', { items: now.items.filter((i) => i.player.id !== id) })
      invalidate('board:', 'thread:', 'chat:', 'challenge:', 'edchallenges:', 'mychallenges')
      toast(<><Icon name="check" />Photo taken down</>)
    } catch {
      toast('Couldn’t take that down')
    }
  }
  return (
    <section className="you-sec">
      <div className="sec-head"><h3>Uploaded photos</h3><span className="faint small">newest first</span></div>
      {uploads.error ? <ErrorNote error={uploads.error} retry={() => uploads.reload()} />
        : !uploads.data ? <p className="faint small">Loading…</p>
        : uploads.data.items.length === 0 ? <p className="faint small">No uploads yet.</p>
        : (
          <div className="uploads">
            {uploads.data.items.map((i) => (
              <div className="upload" key={i.player.id}>
                <img src={i.avatar} alt={`${i.player.displayName}’s photo`} width={72} height={72} loading="lazy" />
                <b className="ellipsis">{i.player.displayName}</b>
                <span className="faint mono ellipsis">{i.player.email ?? 'no email'}</span>
                <button type="button" className="btn plain sm" onClick={() => remove(i.player.id, i.player.displayName)}>Take down</button>
              </div>
            ))}
          </div>
        )}
    </section>
  )
}

/**
 * New visitors per day and first-touch source: how many played a word, how far they got, and how
 * many finished and came back the next day.
 */
function Sources() {
  const sources = useAdminSources()
  const data = sources.data
  const days: SourceRow[][] = []
  for (const row of data?.rows ?? []) {
    if (days.at(-1)?.[0].date === row.date) days.at(-1)!.push(row)
    else days.push([row])
  }
  return (
    <section className="you-sec">
      <div className="sec-head"><h3>New visitors by source</h3><span className="faint small">last 14 days</span></div>
      {sources.error ? <ErrorNote error={sources.error} retry={() => sources.reload()} />
        : !data ? <p className="faint empty-note">Counting…</p>
        : days.length === 0 ? <p className="muted empty-note">No new visitors in the last 14 days.</p>
        : (
          <table className="src-table">
            <thead><tr><th>Source / campaign</th><th>Visitors</th><th>Played<br />a word</th><th>Median rows<br />reached</th><th>Finished<br />that day</th><th>Back<br />next day</th></tr></thead>
            {days.map((rows) => (
              <tbody key={rows[0].date}>
                <tr className="src-day">
                  <th colSpan={6}>
                    {rows[0].returned === null ? 'Today' : day(rows[0].date)} · No. {rows[0].edition}
                    <span>{fmt(rows.reduce((sum, r) => sum + r.players, 0))} visitors</span>
                  </th>
                </tr>
                {rows.map((r) => (
                  <tr key={`${r.source}|${r.campaign}|${r.referrer}`}>
                    <td className={r.source ? '' : 'faint'}>{sourceLabel(r)}</td>
                    <td>{fmt(r.players)}</td>
                    <td><Share n={r.played} of={r.players} /></td>
                    <td>{r.medianRows === null ? <span className="faint">—</span> : fmt(r.medianRows)}</td>
                    <td><Share n={r.finished} of={r.players} /></td>
                    <td>{r.returned === null ? <span className="faint">—</span> : <Share n={r.returned} of={r.players} />}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        )}
    </section>
  )
}

/**
 * The daily reminder: browsers that have it on, each day's reminders by push service (taken to
 * deliver, reached the browser, put off by an outage, failed), and the latest pushes, tests too.
 */
function Reminders() {
  const reminders = useAdminReminders()
  const data = reminders.data
  const days: ReminderDay[][] = []
  for (const row of data?.days ?? []) {
    if (days.at(-1)?.[0].date === row.date) days.at(-1)!.push(row)
    else days.push([row])
  }
  const browsers = data?.on.reduce((sum, o) => sum + o.browsers, 0) ?? 0
  return (
    <section className="you-sec">
      <div className="sec-head"><h3>Daily reminders</h3><span className="faint small">last 14 days</span></div>
      {reminders.error ? <ErrorNote error={reminders.error} retry={() => reminders.reload()} />
        : !data ? <p className="faint empty-note">Counting…</p>
        : (
          <>
            <p className="muted small rem-on">
              On in {fmt(browsers)} browser{browsers === 1 ? '' : 's'}{data.on.length > 0 && `: ${data.on.map((o) => `${o.service} ${fmt(o.browsers)}`).join(' · ')}`}
            </p>
            {days.length === 0 ? <p className="muted empty-note">No reminders sent in the last 14 days.</p> : (
              <table className="src-table">
                <thead><tr><th>Push service</th><th>Sent</th><th>Reached<br />the browser</th><th>Outage,<br />retried</th><th>Failed</th></tr></thead>
                {days.map((rows) => (
                  <tbody key={rows[0].date}>
                    <tr className="src-day">
                      <th colSpan={5}>{day(rows[0].date)}<span>{fmt(rows.reduce((sum, r) => sum + r.sent, 0))} sent</span></th>
                    </tr>
                    {rows.map((r) => (
                      <tr key={r.service}>
                        <td>{r.service}</td>
                        <td>{fmt(r.sent)}</td>
                        <td>{r.sent ? <Share n={r.received} of={r.sent} /> : <span className="faint">—</span>}</td>
                        <td>{fmt(r.retried)}</td>
                        <td>{fmt(r.failed)}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            )}
            <p className="faint small rem-note">Reached the browser: the site’s service worker said the notification arrived.</p>
            {data.recent.length > 0 && (
              <>
                <h4 className="rem-sub">Latest pushes</h4>
                <table className="src-table">
                  <thead><tr><th>Player</th><th>Sent</th><th>Answer</th><th>Reached</th></tr></thead>
                  <tbody>
                    {data.recent.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.player.displayName}
                          <small className="rem-who">{r.player.email ?? 'guest'} · {r.kind === 'test' ? 'test' : `No. ${r.edition}`} · {r.service}</small>
                        </td>
                        <td>{when(r.sentAt)}</td>
                        <td className={r.outcome === 'sent' ? '' : 'rem-bad'}>{OUTCOMES[r.outcome]} {r.status ?? <span className="faint">no answer</span>}</td>
                        <td>{r.receivedAt ? lag(r.sentAt, r.receivedAt) : <span className="faint">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
    </section>
  )
}

const OUTCOMES: Record<ReminderOutcome, string> = { sent: 'Accepted', retry: 'Outage', ended: 'Gone', refused: 'Refused' }

/** How long a push took to reach the browser: "<1s", "+4s", "+12m", "+3h 5m". */
function lag(sent: string, received: string) {
  const s = Math.round((Date.parse(received) - Date.parse(sent)) / 1000)
  if (s < 1) return '<1s'
  if (s < 60) return `+${s}s`
  const m = Math.round(s / 60)
  return m < 60 ? `+${m}m` : `+${Math.floor(m / 60)}h ${m % 60}m`
}

const sourceLabel = (r: SourceRow) =>
  r.source ? (r.campaign ? `${r.source} / ${r.campaign}` : r.source) : r.referrer ? `via ${r.referrer}` : 'direct'

const Share = ({ n, of }: { n: number; of: number }) => <>{fmt(n)}<small>{Math.round((n / of) * 100)}%</small></>

/** A YYYY-MM-DD as "Sat, Oct 17". */
const day = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

function FeedbackList({ status }: { status: FeedbackStatus }) {
  const key = `admin:feedback:${status}`
  const list = useAdminFeedback(status)
  const [loadingMore, setLoadingMore] = useState(false)
  const toast = useToast()
  const page = list.data

  async function more() {
    if (!page?.next) return
    setLoadingMore(true)
    try {
      const next = await api.get<FeedbackPage>(`/admin/feedback?status=${status}&before=${page.next}`)
      const current = getResource<FeedbackPage>(key)
      setResource<FeedbackPage>(key, { items: [...(current?.items ?? []), ...next.items], next: next.next })
    } catch {
      toast('Couldn’t load more')
    } finally {
      setLoadingMore(false)
    }
  }

  async function toggle(item: FeedbackItem) {
    try {
      await api.patch<FeedbackItem>(`/admin/feedback/${item.id}`, { done: item.doneAt === null })
      const current = getResource<FeedbackPage>(key)
      if (current) setResource<FeedbackPage>(key, { ...current, items: current.items.filter((i) => i.id !== item.id) })
      invalidate(`admin:feedback:${status === 'open' ? 'done' : 'open'}`, 'admin:overview')
      toast(<><Icon name="check" />{item.doneAt === null ? 'Marked done' : 'Moved back to open'}</>)
    } catch {
      toast('Couldn’t update that')
    }
  }

  if (list.error) return <ErrorNote error={list.error} retry={() => list.reload()} />
  if (!page) return <p className="faint empty-note">Opening the mail…</p>
  if (page.items.length === 0) {
    return <p className="muted empty-note">{status === 'open' ? 'Nothing waiting. Every note has been read.' : 'Nothing marked done yet.'}</p>
  }
  return (
    <div className="fb-list">
      {page.items.map((f) => <FeedbackRow key={f.id} f={f} onToggle={() => toggle(f)} />)}
      {page.next && <button type="button" className="btn ghost sm" onClick={more} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</button>}
    </div>
  )
}

const MODE_NAME: Record<RatingMode, string> = { daily: 'Daily', slow_burn: 'Slow Burn' }

/** What everyone each mode's rating ask opened for did with it; "% up" is of those who gave a thumbs. */
function RatingTallies({ ratings }: { ratings: AdminOverview['ratings'] }) {
  return (
    <table className="src-table rate-table">
      <thead>
        <tr>
          <th>Rating ask</th>
          <th><Icon name="thumbup" size={14} /><span className="sr-only">Up</span></th>
          <th><Icon name="thumbdown" size={14} /><span className="sr-only">Down</span></th>
          <th>% up</th><th>Rather not say</th><th>Closed it</th><th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {RATING_MODES.map((m) => {
          const t = ratings[m]
          const thumbs = t.up + t.down
          return (
            <tr key={m}>
              <td>{MODE_NAME[m]}</td><td>{fmt(t.up)}</td><td>{fmt(t.down)}</td>
              <td>{thumbs ? `${Math.round((t.up / thumbs) * 100)}%` : '—'}</td>
              <td>{fmt(t.none)}</td><td>{fmt(t.closed)}</td><td>{fmt(t.notes)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function FeedbackRow({ f, onToggle }: { f: FeedbackItem; onToggle(): void }) {
  const who = f.player
  return (
    <div className="cm">
      <Avatar id={who?.id} name={who?.displayName ?? '?'} />
      <div>
        <div className="hd">
          <span className="who">
            <b>{who?.displayName ?? 'Anonymous'}</b>
            {f.mode && f.rating && (
              <span className={`fb-from ${f.rating}`}><Icon name={f.rating === 'up' ? 'thumbup' : 'thumbdown'} />{MODE_NAME[f.mode]}{f.edition !== null && ` · No. ${f.edition}`}</span>
            )}
          </span>
          <span className="em">{who ? who.email ?? 'Guest' : 'No session'}</span>
          <span className="tm">{when(f.createdAt)}</span>
        </div>
        <p>{f.body}</p>
        <div className="ft">
          <button type="button" onClick={onToggle}><Icon name={f.doneAt === null ? 'check' : 'refresh'} />{f.doneAt === null ? 'Mark done' : 'Reopen'}</button>
          {who?.email && <a href={`mailto:${who.email}?subject=${encodeURIComponent('Your Letters on Fire feedback')}`}><Icon name="mail" />Email them</a>}
          {f.doneAt !== null && <span className="done-at">done {when(f.doneAt)}</span>}
        </div>
      </div>
    </div>
  )
}

const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
